import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { BaseModal } from '../../../../../../components/BaseModal';
import { useNotification } from '../../../../../../contexts/NotificationContext';

export enum DuplicateOption {
  BUYER = 'buyer',
  SUPPLIER = 'supplier',
}

interface DuplicateChatModalProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  chatData: {
    buyerEntity?: {
      id: string;
      name: string;
    } | null;
    supplierEntity?: {
      id: string;
      name: string;
    } | null;
  };
}

export const DuplicateChatModal: React.FC<DuplicateChatModalProps> = ({
  isOpen,
  onClose,
  chatId,
  chatData,
}) => {
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const [selectedOption, setSelectedOption] = useState<DuplicateOption>(
    DuplicateOption.BUYER
  );
  const [duplicateOrderGuide, setDuplicateOrderGuide] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleDuplicate = async () => {
    try {
      setIsLoading(true);

      // Build the query parameters
      const params = new URLSearchParams({
        duplicateFrom: chatId,
        duplicateOption: selectedOption,
        duplicateOrderGuide: duplicateOrderGuide.toString(),
      });

      // Navigate to create chat page with the parameters
      navigate(`/chats/create?${params.toString()}`);

      addNotification({
        title: 'Success',
        message: 'Redirecting to create chat page with duplicated data...',
        type: 'success',
      });
    } catch (error) {
      addNotification({
        title: 'Error',
        message:
          error instanceof Error ? error.message : 'Failed to duplicate chat',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      title="I want to create a chat for..."
      footerContent={{
        cancelText: 'Cancel',
        confirmText: 'Create',
        onCancel: onClose,
        onConfirm: handleDuplicate,
        isLoading,
      }}
    >
      <div className="space-y-6">
        {/* Buyer Option */}
        <label className="flex items-center justify-between p-4 bg-content2 rounded-lg cursor-pointer hover:bg-content3 transition-colors">
          <div className="flex-1">
            <p className="text-lg font-medium text-foreground">
              {chatData.buyerEntity?.name || 'Same buyer'}
            </p>
            <p className="text-sm text-foreground-500">
              Create a new chat with the same buyer but different supplier
            </p>
          </div>
          <input
            type="radio"
            name="duplicateOption"
            value={DuplicateOption.BUYER}
            checked={selectedOption === DuplicateOption.BUYER}
            onChange={(e) =>
              setSelectedOption(e.target.value as DuplicateOption)
            }
            className="form-radio h-5 w-5 text-primary"
          />
        </label>

        {/* Supplier Option */}
        <div className="space-y-2">
          <label className="flex items-center justify-between p-4 bg-content2 rounded-lg cursor-pointer hover:bg-content3 transition-colors">
            <div className="flex-1">
              <p className="text-lg font-medium text-foreground">
                {chatData.supplierEntity?.name || 'Same supplier'}
              </p>
              <p className="text-sm text-foreground-500">
                Create a new chat with the same supplier but different buyer
              </p>
            </div>
            <input
              type="radio"
              name="duplicateOption"
              value={DuplicateOption.SUPPLIER}
              checked={selectedOption === DuplicateOption.SUPPLIER}
              onChange={(e) =>
                setSelectedOption(e.target.value as DuplicateOption)
              }
              className="form-radio h-5 w-5 text-primary"
            />
          </label>

          {selectedOption === DuplicateOption.SUPPLIER && (
            <label className="flex items-center justify-between px-4 py-2 ml-4 border-l-2 border-content3">
              <span className="text-sm text-foreground-500">
                Duplicate order guide (max 500 products)
              </span>
              <input
                type="checkbox"
                checked={duplicateOrderGuide}
                onChange={(e) => setDuplicateOrderGuide(e.target.checked)}
                className="form-checkbox h-4 w-4 text-primary"
                disabled={selectedOption !== DuplicateOption.SUPPLIER}
              />
            </label>
          )}
        </div>

        <div className="text-sm text-foreground-500 p-4 bg-content1 rounded-lg">
          Selecting one of the options above will auto-magically fit all the
          relevant fields so you can operate 10x faster! Don't worry, you'll be
          able to edit all the fields before saving the chat.
        </div>
      </div>
    </BaseModal>
  );
};
